import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.body.addEventListener('pointermove', this.#trackPointerPosition, { passive: true });
    document.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.body.removeEventListener('pointermove', this.#trackPointerPosition);
    document.removeEventListener('scroll', this.#onScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation, and for
   * scroll-triggered reconciliation (see #onScroll below).
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * rAF id for the throttled scroll handler, or null when none is pending.
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Update the safety box idle state on the active menu item.
   * `moving` uses Math.abs() on both axes so upward and leftward movement get the
   * same 50ms debounce/safety-box grace period as downward and rightward movement --
   * without it, movementY's sign meant only downward motion counted as "moving".
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Lightweight, always-on pointer tracking (regardless of whether a menu is
   * currently open), so #lastPointer is available for scroll-triggered
   * reconciliation even before any menu has ever been activated. Deliberately
   * separate from #onPointerMove above, which additionally drives the
   * safety-box idle timer and must keep its existing open-menu-only scope
   * and timing unchanged.
   * @param {PointerEvent} event
   */
  #trackPointerPosition = (event) => {
    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;
  };

  /**
   * rAF-throttled scroll handler. Browsers do not dispatch pointermove/
   * pointerenter/pointerleave when the page scrolls under a stationary
   * cursor, so hover-driven menu state can go stale (e.g. a nav item
   * scrolling under the cursor doesn't open its menu; an open menu doesn't
   * close/switch when scrolled out from under the cursor). Re-run the same
   * elementFromPoint reconciliation used for the Safari case above, using
   * the last known pointer position, regardless of whether a menu is
   * currently open.
   */
  #onScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#reconcilePointerTarget();
    });
  };

  /**
   * Check whether the pointer is over a different menu item (or no longer
   * over any menu item) and open/switch/close accordingly.
   * Works around Safari not re-evaluating hit targets after pseudo-element
   * changes, and covers scroll-under-stationary-cursor via #onScroll above.
   *
   * The "is the pointer still relevant to the active item" check is done against
   * live geometry (getBoundingClientRect of the trigger and, separately, of its
   * open dropdown panel), not DOM containment (.closest()/.contains()) against
   * the whole <li>. Checking the trigger alone would incorrectly close the menu
   * the moment the pointer moves from the trigger into its own dropdown; checking
   * DOM containment alone can't tell "pointer is still relevant to this trigger"
   * apart from "pointer happens to overlap the still-rendered dropdown after the
   * trigger scrolled away". The dropdown panel is positioned (top: calc(...))
   * relative to the sticky header, so its rect moves in sync with the trigger --
   * using its live rect (rather than "is it in the DOM subtree") correctly
   * distinguishes genuinely hovering the currently-displayed dropdown from the
   * trigger and dropdown having both scrolled away from a stationary pointer.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const activeItem = this.#state.activeItem;

      if (activeItem && this.#isPointerOverActiveArea(x, y, activeItem)) return;

      const target = document.elementFromPoint(x, y);
      const listItem = target instanceof Element ? target.closest('.menu-list__list-item') : null;

      if (listItem && !listItem.contains(activeItem)) {
        // Pointer is over a different top-level item's trigger or dropdown -> open/switch to it.
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      } else if (activeItem) {
        // Pointer is outside both the active trigger and its dropdown (checked above), and isn't
        // over a different item either -> close, even if elementFromPoint still resolves inside
        // the active item's own (now-vacated) open dropdown panel.
        this.#deactivate();
      }
    });
  }

  /**
   * Whether (x, y) falls inside the active item's trigger rect, its currently
   * open dropdown panel's rect, or the narrow vertical gap between the two.
   *
   * The panel rect is read from .menu-list__submenu-inner, not the outer
   * .menu-list__submenu (what findSubmenu() returns). The outer wrapper is
   * positioned via `top: calc(...)` and clipped via `clip-path` to the open
   * window, but .menu-list__submenu-inner is the child that's actually
   * `transform: translateY(...)`-shifted to sit inside that clipped window --
   * a transform on a child doesn't change the parent's own layout box, so the
   * outer wrapper's getBoundingClientRect() reflects its pre-transform layout
   * position, not where the panel is actually painted. Confirmed directly: the
   * outer wrapper's rect (e.g. top 29/bottom 241 in one measurement) didn't
   * match either the clip-path window or where elementFromPoint found real
   * content, while .menu-list__submenu-inner's rect (top 116/bottom 328 in the
   * same measurement) matched both exactly.
   *
   * The trigger's own rect is extended down to the panel's top edge (within the
   * trigger's own horizontal span, not the panel's full width) to bridge the
   * small vertical gap between the nav row and the panel starting -- WebKit was
   * confirmed to report movementX/movementY as 0 for every intermediate pointer
   * sample, which skips the 50ms debounce entirely (see #onPointerMove) and
   * reconciles on every sample, so a real continuous downward drag could
   * momentarily land in that gap and close the menu before Chromium's debounce
   * would ever let that matter.
   * @param {number} x
   * @param {number} y
   * @param {HTMLElement} activeItem
   * @returns {boolean}
   */
  #isPointerOverActiveArea(x, y, activeItem) {
    const triggerRect = activeItem.getBoundingClientRect();
    const submenu = findSubmenu(activeItem);
    const visiblePanel = submenu?.querySelector('.menu-list__submenu-inner') ?? submenu;
    const panelRect = visiblePanel?.getBoundingClientRect();

    const extendedTriggerRect = panelRect
      ? {
          left: triggerRect.left,
          right: triggerRect.right,
          top: triggerRect.top,
          bottom: Math.max(triggerRect.bottom, panelRect.top),
        }
      : triggerRect;

    if (pointInRect(x, y, extendedTriggerRect)) return true;
    return panelRect ? pointInRect(x, y, panelRect) : false;
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Chromium's own native hit-testing sometimes fires a real, untrusted-by-us pointerleave
    // at the exact boundary between the trigger <li> and where the dropdown panel starts
    // visually (a few px gap between the two, independent of any of the reconciliation logic
    // above) -- confirmed directly: the menu closed with no #reconcilePointerTarget call in
    // between at all, purely from this native event. If the pointer's actual current position
    // is still within the active trigger+panel area, treat that the same as the geometric
    // checks elsewhere in this file and ignore the native leave rather than closing.
    if (
      event instanceof PointerEvent &&
      this.#state.activeItem &&
      this.#isPointerOverActiveArea(event.clientX, event.clientY, this.#state.activeItem)
    ) {
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Whether a point falls inside a DOMRect.
 * @param {number} x
 * @param {number} y
 * @param {DOMRect} rect
 * @returns {boolean}
 */
function pointInRect(x, y, rect) {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}
